import {
	Actor,
	type ActorSubclass,
	type HttpAgent,
} from "@icp-sdk/core/agent";
import { IDL } from "@icp-sdk/core/candid";
import { Principal } from "@icp-sdk/core/principal";
import type { IcClientConfig } from "./config";
import {
	amountToNat,
	type CollateralKey,
	natToAmount,
} from "./candid-helpers";
import { formatCandidError } from "./result";

const icrcLedgerIdlFactory: IDL.InterfaceFactory = ({ IDL: I }) => {
	const Subaccount = I.Vec(I.Nat8);
	const Account = I.Record({
		owner: I.Principal,
		subaccount: I.Opt(Subaccount),
	});
	const TransferArgs = I.Record({
		from_subaccount: I.Opt(Subaccount),
		to: Account,
		amount: I.Nat,
		fee: I.Opt(I.Nat),
		memo: I.Opt(I.Vec(I.Nat8)),
		created_at_time: I.Opt(I.Nat64),
	});
	const TransferError = I.Variant({
		BadFee: I.Record({ expected_fee: I.Nat }),
		BadBurn: I.Record({ min_burn_amount: I.Nat }),
		InsufficientFunds: I.Record({ balance: I.Nat }),
		TooOld: I.Null,
		CreatedInFuture: I.Record({ ledger_time: I.Nat64 }),
		Duplicate: I.Record({ duplicate_of: I.Nat }),
		TemporarilyUnavailable: I.Null,
		GenericError: I.Record({ error_code: I.Nat, message: I.Text }),
	});
	const ApproveArgs = I.Record({
		from_subaccount: I.Opt(Subaccount),
		spender: Account,
		amount: I.Nat,
		expected_allowance: I.Opt(I.Nat),
		expires_at: I.Opt(I.Nat64),
		fee: I.Opt(I.Nat),
		memo: I.Opt(I.Vec(I.Nat8)),
		created_at_time: I.Opt(I.Nat64),
	});
	const ApproveError = I.Variant({
		BadFee: I.Record({ expected_fee: I.Nat }),
		InsufficientFunds: I.Record({ balance: I.Nat }),
		AllowanceChanged: I.Record({ current_allowance: I.Nat }),
		Expired: I.Record({ ledger_time: I.Nat64 }),
		TooOld: I.Null,
		CreatedInFuture: I.Record({ ledger_time: I.Nat64 }),
		Duplicate: I.Record({ duplicate_of: I.Nat }),
		TemporarilyUnavailable: I.Null,
		GenericError: I.Record({ error_code: I.Nat, message: I.Text }),
	});
	return I.Service({
		icrc1_balance_of: I.Func([Account], [I.Nat], ["query"]),
		icrc1_fee: I.Func([], [I.Nat], ["query"]),
		icrc1_transfer: I.Func(
			[TransferArgs],
			[I.Variant({ Ok: I.Nat, Err: TransferError })],
			[],
		),
		icrc2_approve: I.Func(
			[ApproveArgs],
			[I.Variant({ Ok: I.Nat, Err: ApproveError })],
			[],
		),
	});
};

type IcrcLedger = ActorSubclass<{
	icrc1_balance_of: (account: {
		owner: Principal;
		subaccount: [] | [Uint8Array];
	}) => Promise<bigint>;
	icrc1_fee: () => Promise<bigint>;
	icrc1_transfer: (args: {
		from_subaccount: [] | [Uint8Array];
		to: { owner: Principal; subaccount: [] | [Uint8Array] };
		amount: bigint;
		fee: [] | [bigint];
		memo: [] | [Uint8Array];
		created_at_time: [] | [bigint];
	}) => Promise<{ Ok: bigint } | { Err: unknown }>;
	icrc2_approve: (args: {
		from_subaccount: [] | [Uint8Array];
		spender: { owner: Principal; subaccount: [] | [Uint8Array] };
		amount: bigint;
		expected_allowance: [] | [bigint];
		expires_at: [] | [bigint];
		fee: [] | [bigint];
		memo: [] | [Uint8Array];
		created_at_time: [] | [bigint];
	}) => Promise<{ Ok: bigint } | { Err: unknown }>;
}>;

function ledgerActor(canisterId: string, agent: HttpAgent): IcrcLedger {
	return Actor.createActor(icrcLedgerIdlFactory, { agent, canisterId });
}

export const DEFAULT_ICRC_TRANSFER_FEE_E8 = 10_000n;

export type TransferTokenId =
	| "ICP"
	| "ckBTC"
	| "ckETH"
	| "ckUSDC"
	| "USDM"
	| "MMK";

export function resolveCollateralLedgerId(
	config: IcClientConfig,
	collateral: CollateralKey,
): string | null {
	const { ledgers } = config;
	if (collateral === "ICP") return ledgers.icp ?? null;
	if (collateral === "ckBTC") return ledgers.ckBtc ?? null;
	if (collateral === "ckETH") return ledgers.ckEth ?? null;
	return null;
}

export function resolveTransferLedgerId(
	config: IcClientConfig,
	token: TransferTokenId,
	chainLedgers?: {
		usdm: [] | [Principal];
		mmk: [] | [Principal];
	},
): string | null {
	if (token === "USDM") {
		const chain = chainLedgers?.usdm[0]?.toText();
		return chain ?? config.ledgers.usdm ?? null;
	}
	if (token === "MMK") {
		const chain = chainLedgers?.mmk[0]?.toText();
		return chain ?? config.ledgers.mmk ?? null;
	}
	if (token === "ICP") return config.ledgers.icp ?? null;
	if (token === "ckBTC") return config.ledgers.ckBtc ?? null;
	if (token === "ckETH") return config.ledgers.ckEth ?? null;
	if (token === "ckUSDC") return config.ledgers.ckUsdc ?? null;
	return null;
}

export async function fetchIcrcBalance(
	agent: HttpAgent,
	ledgerCanisterId: string,
	owner: Principal,
	subaccount?: Uint8Array | null,
): Promise<number> {
	const ledger = ledgerActor(ledgerCanisterId, agent);
	const sub: [] | [Uint8Array] =
		subaccount && subaccount.length > 0 ? [subaccount] : [];
	const balance = await ledger.icrc1_balance_of({
		owner,
		subaccount: sub,
	});
	return natToAmount(balance);
}

/** Raw e8 balance (for exact prepay checks). */
export async function fetchIcrcBalanceE8(
	agent: HttpAgent,
	ledgerCanisterId: string,
	owner: Principal,
	subaccount?: Uint8Array | null,
): Promise<bigint> {
	const ledger = ledgerActor(ledgerCanisterId, agent);
	const sub: [] | [Uint8Array] =
		subaccount && subaccount.length > 0 ? [subaccount] : [];
	return ledger.icrc1_balance_of({
		owner,
		subaccount: sub,
	});
}

async function fetchIcrcTransferFee(ledger: IcrcLedger): Promise<bigint> {
	try {
		const fee = await ledger.icrc1_fee();
		return fee > 0n ? fee : DEFAULT_ICRC_TRANSFER_FEE_E8;
	} catch {
		return DEFAULT_ICRC_TRANSFER_FEE_E8;
	}
}

/** USDM ledger transfer fee (e8) for escrow→treasury holds and wallet transfers. */
export async function fetchUsdmTransferFee(
	agent: HttpAgent,
	config: IcClientConfig,
	chainLedgers: {
		usdm: [] | [Principal];
		mmk: [] | [Principal];
	},
): Promise<bigint> {
	const ledgerId = resolveTransferLedgerId(config, "USDM", chainLedgers);
	if (!ledgerId) return DEFAULT_ICRC_TRANSFER_FEE_E8;
	return fetchIcrcTransferFee(ledgerActor(ledgerId, agent));
}

export function icrc2ApproveAmountForTransfer(
	amount: number,
	transferFee: bigint,
): bigint {
	return amountToNat(amount) + transferFee;
}

export async function approveIcrcSpender(
	agent: HttpAgent,
	ledgerCanisterId: string,
	spenderCanisterId: string,
	amount: number,
): Promise<void> {
	const ledger = ledgerActor(ledgerCanisterId, agent);
	const transferFee = await fetchIcrcTransferFee(ledger);
	const approveAmount = icrc2ApproveAmountForTransfer(amount, transferFee);
	const fee: [] | [bigint] = transferFee > 0n ? [transferFee] : [];

	const result = await ledger.icrc2_approve({
		from_subaccount: [],
		spender: {
			owner: Principal.fromText(spenderCanisterId),
			subaccount: [],
		},
		amount: approveAmount,
		expected_allowance: [],
		expires_at: [],
		fee,
		memo: [],
		created_at_time: [],
	});

	if ("Err" in result) {
		throw new Error(`icrc2_approve: ${formatCandidError(result.Err)}`);
	}
}

export async function approveUsdmForProtocol(
	agent: HttpAgent,
	config: IcClientConfig,
	chainLedgers: {
		usdm: [] | [Principal];
		mmk: [] | [Principal];
	},
	amount: number,
): Promise<void> {
	const ledgerId = resolveTransferLedgerId(config, "USDM", chainLedgers);
	if (!ledgerId) {
		throw new Error("USDM ledger not configured");
	}
	await approveIcrcSpender(
		agent,
		ledgerId,
		config.protocolCanisterId,
		amount,
	);
}

export async function transferIcrc(
	agent: HttpAgent,
	ledgerCanisterId: string,
	to: Principal,
	amount: number,
	toSubaccount?: Uint8Array | null,
): Promise<bigint> {
	const ledger = ledgerActor(ledgerCanisterId, agent);
	const transferFee = await fetchIcrcTransferFee(ledger);
	const fee: [] | [bigint] = transferFee > 0n ? [transferFee] : [];
	const sub: [] | [Uint8Array] =
		toSubaccount && toSubaccount.length > 0 ? [toSubaccount] : [];
	const result = await ledger.icrc1_transfer({
		from_subaccount: [],
		to: { owner: to, subaccount: sub },
		amount: amountToNat(amount),
		fee,
		memo: [],
		created_at_time: [],
	});
	if ("Err" in result) {
		throw new Error(`icrc1_transfer: ${formatCandidError(result.Err)}`);
	}
	return result.Ok;
}

/**
 * Pre-pay USDM into the liquidator's escrow under the protocol principal.
 * Required when the protocol is the USDM minting account (cannot transfer_from).
 */
export async function transferUsdmToLiquidatorEscrow(
	agent: HttpAgent,
	config: IcClientConfig,
	chainLedgers: {
		usdm: [] | [Principal];
		mmk: [] | [Principal];
	},
	amount: number,
	escrow: { owner: Principal; subaccount: Uint8Array },
): Promise<bigint> {
	const ledgerId = resolveTransferLedgerId(config, "USDM", chainLedgers);
	if (!ledgerId) throw new Error("USDM ledger not configured");
	return transferIcrc(
		agent,
		ledgerId,
		escrow.owner,
		amount,
		escrow.subaccount,
	);
}
